// Praxis-API-Client. Spricht das Bertsy-Backend mit Praxis-Session an.

// Default = Produktions-API. Override für lokale Entwicklung via VITE_API_BASE_URL.
// Vorher war der Default localhost:8787 — das hat in produktiven Builds ohne
// gesetzte Env-Variable das Praxis-Dashboard ins Leere laufen lassen.
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.bertsy.de';
const TOKEN_KEY = 'bertsy_praxis_session';

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else sessionStorage.removeItem(TOKEN_KEY);
}

async function call<T>(path: string, init?: RequestInit, requireAuth = true): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (requireAuth) {
    const token = getToken();
    if (!token) throw new Error('not_authenticated');
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  let json: any;
  try { json = await res.json(); } catch { throw new Error(`HTTP ${res.status}`); }
  if (!res.ok || !json.ok) {
    if (res.status === 401) setToken(null);
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  return json;
}

export type Fachrichtung =
  | 'allgemeinmedizin' | 'hno' | 'kinderarzt' | 'internist' | 'augenarzt'
  | 'zahnarzt' | 'orthopaede' | 'gynaekologe' | 'hautarzt' | 'urologe'
  | 'psychiater' | 'neurologe' | 'andere';

export interface Praxis {
  id: string;
  name: string;
  email: string;
  telefon?: string;
  adresse?: string;
  notiz?: string;
  website?: string;
  booking_url?: string;
  fachrichtung?: Fachrichtung;
  lat?: number;
  lon?: number;
  oeffnungszeiten?: string;
  chat_disabled?: boolean;
  urlaub_von?: string;
  urlaub_bis?: string;
  urlaub_nachricht?: string;
  qr_token: string;
  created_at: string;
  updated_at: string;
}

export async function login(email: string, password: string) {
  const r = await call<{ ok: true; token: string; praxis: { id: string; name: string; email: string } }>(
    '/api/praxis/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
    false,
  );
  setToken(r.token);
  return r;
}

export async function logout(): Promise<void> {
  try { await call('/api/praxis/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
  setToken(null);
}

export async function getMe() {
  return call<{ ok: true; praxis: Praxis }>('/api/praxis/auth/me');
}

export async function updateMe(patch: {
  name?: string;
  telefon?: string | null;
  adresse?: string | null;
  notiz?: string | null;
  website?: string | null;
  booking_url?: string | null;
  fachrichtung?: Fachrichtung | null;
  lat?: number | null;
  lon?: number | null;
  oeffnungszeiten?: string | null;
  chat_disabled?: boolean;
  urlaub_von?: string | null;
  urlaub_bis?: string | null;
  urlaub_nachricht?: string | null;
  password?: string;
}) {
  return call<{ ok: true; praxis: Praxis }>('/api/praxis/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ─── Patient bearbeiten / sperren ───────────────────────────────────

export async function updatePatient(patientId: string, patch: {
  chat_banned?: boolean;
  chat_ban_reason?: string | null;
  praxis_notiz?: string | null;
  pii?: {
    vorname?: string;
    nachname?: string;
    geburtsdatum?: string;
    email?: string;
  };
}) {
  return call<{ ok: true; patient: PatientPublic }>(
    `/api/praxis/patienten/${encodeURIComponent(patientId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

export type AnfrageTyp = 'krankmeldung' | 'folgerezept' | 'ueberweisung';

export interface KrankmeldungAnfrage {
  id: string;
  typ?: AnfrageTyp; // optional für Backwards-Kompat (alte Einträge = krankmeldung)
  praxis_id: string;
  patient: { vorname: string; nachname: string; geburtsdatum?: string; versicherten_nr?: string };
  anfordernder: { name: string; email: string; rolle: string };
  beschwerde: string;
  rezept_id?: string;
  zeitraum: { von: string; bis: string };
  hinweis?: string;
  folgerezept?: {
    medikament: string;
    staerke?: string;
    anwendung?: string;
    bestehende_verordnung?: string;
  };
  ueberweisung?: {
    fachrichtung: string;
    grund: string;
    wunsch_praxis?: string;
    dringlichkeit?: 'normal' | 'eilig';
  };
  status: 'eingegangen' | 'bestaetigt' | 'abgelehnt' | 'erledigt';
  praxis_notiz?: string;
  empfangen_am: string;
  bearbeitet_am?: string;
}

export interface InboxStats {
  total: number;
  eingegangen: number;
  bestaetigt: number;
  abgelehnt: number;
  erledigt: number;
}

export async function getInbox() {
  return call<{ ok: true; anfragen: KrankmeldungAnfrage[]; stats: InboxStats }>('/api/praxis/inbox');
}

export async function updateAnfrage(id: string, patch: { status?: KrankmeldungAnfrage['status']; praxis_notiz?: string }) {
  return call<{ ok: true; anfrage: KrankmeldungAnfrage }>(`/api/praxis/inbox/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteAnfrage(id: string) {
  return call(`/api/praxis/inbox/${id}`, { method: 'DELETE' });
}

// ─── Patienten ──────────────────────────────────────────────────────

export interface PatientPublic {
  id: string;
  praxis_id: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  email: string;
  consent_at: string;
  consent_version: string;
  created_at: string;
  last_seen_at: string;
  has_push: boolean;
  chat_banned?: boolean;
  chat_ban_reason?: string;
  praxis_notiz?: string;
}

export async function listPatienten() {
  return call<{ ok: true; patienten: PatientPublic[] }>('/api/praxis/patienten');
}

export async function getPatient(id: string) {
  // Backend liefert volle Thread-Inhalte inkl. aller Nachrichten + Attachments
  return call<{
    ok: true;
    patient: PatientPublic;
    anfragen: KrankmeldungAnfrage[];
    threads: FrageThread[];
  }>(`/api/praxis/patienten/${encodeURIComponent(id)}`);
}

export async function revokePatient(id: string) {
  return call(`/api/praxis/patienten/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function terminRufPatient(id: string) {
  return call<{ ok: boolean }>(`/api/praxis/patienten/${encodeURIComponent(id)}/termin-ruf`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ─── Fragen-Threads ─────────────────────────────────────────────────

export interface FrageMessage {
  id: string;
  from: 'patient' | 'praxis';
  text: string;
  ts: string;
  gelesen_am?: string;
  attachments?: string[];
}

const API_BASE_RESOLVED = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? API_BASE;
export function uploadImageUrl(id: string): string {
  return `${API_BASE_RESOLVED}/api/uploads/${encodeURIComponent(id)}`;
}
export function uploadAuthHeader(): { Authorization: string } | {} {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export interface FrageThread {
  id: string;
  patient_id: string;
  praxis_id: string;
  rezept_id?: string;
  titel: string;
  status: 'offen' | 'beantwortet' | 'geschlossen';
  messages: FrageMessage[];
  created_at: string;
  updated_at: string;
  ungelesen_count: number;
  patient?: { id: string; name: string };
}

export async function listFragen() {
  return call<{ ok: true; threads: FrageThread[] }>('/api/praxis/fragen');
}

export async function getFrage(id: string) {
  return call<{
    ok: true;
    thread: FrageThread;
    patient: { id: string; vorname: string; nachname: string; geburtsdatum: string; email: string } | null;
  }>(`/api/praxis/fragen/${encodeURIComponent(id)}`);
}

export async function replyFrage(id: string, text: string, attachments?: string[]) {
  return call<{ ok: true; thread: FrageThread }>(
    `/api/praxis/fragen/${encodeURIComponent(id)}/messages`,
    { method: 'POST', body: JSON.stringify({ text, attachments }) },
  );
}

export async function closeFrage(id: string) {
  return call(`/api/praxis/fragen/${encodeURIComponent(id)}/close`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── Termin-Vorschläge ──────────────────────────────────────────────

export type TerminStatus = 'offen' | 'angenommen' | 'abgelehnt' | 'storniert';

export interface TerminVorschlag {
  id: string;
  praxis_id: string;
  patient_id: string;
  patient_name?: string;
  termin_datum: string;
  termin_uhrzeit: string;
  notiz?: string;
  status: TerminStatus;
  vorgeschlagen_am: string;
  beantwortet_am?: string;
  patient_kommentar?: string;
  thread_id?: string;
}

export async function vorschlagTermin(
  patientId: string,
  args: { termin_datum: string; termin_uhrzeit: string; notiz?: string; thread_id?: string },
) {
  return call<{ ok: true; termin: TerminVorschlag }>(
    `/api/praxis/patienten/${encodeURIComponent(patientId)}/termin-vorschlag`,
    { method: 'POST', body: JSON.stringify(args) },
  );
}

export async function listTermine() {
  return call<{ ok: true; termine: TerminVorschlag[] }>('/api/praxis/termine');
}

/**
 * Markiert alle beantworteten Termine als "von Praxis gesehen".
 * Wird beim Öffnen des Termine-Tabs aufgerufen — Badge verschwindet danach.
 */
export async function markTermineAlsGesehen() {
  return call<{ ok: true; updated: number }>('/api/praxis/termine/mark-seen', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function stornoTermin(terminId: string) {
  return call<{ ok: true; termin: TerminVorschlag }>(
    `/api/praxis/termine/${encodeURIComponent(terminId)}`,
    { method: 'DELETE' },
  );
}

// ─── Freie Slots ───────────────────────────────────────────────────

export type SlotStatus = 'frei' | 'gebucht' | 'storniert';

export interface Slot {
  id: string;
  praxis_id: string;
  termin_datum: string;
  termin_uhrzeit: string;
  dauer_minuten: number;
  notiz?: string;
  status: SlotStatus;
  gebucht_von_patient_id?: string;
  gebucht_am?: string;
  patient_kommentar?: string;
  patient_name?: string | null;
  erstellt_am: string;
}

export async function listSlots() {
  return call<{ ok: true; slots: Slot[] }>('/api/praxis/slots');
}

export async function createSlot(args: {
  termin_datum: string;
  termin_uhrzeit: string;
  dauer_minuten?: number;
  notiz?: string;
}) {
  return call<{ ok: true; slot: Slot }>('/api/praxis/slots', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function createSlotsBulk(args: {
  termin_datum: string;
  uhrzeiten: string[];
  dauer_minuten?: number;
  notiz?: string;
}) {
  return call<{ ok: true; created: Slot[]; skipped: string[] }>('/api/praxis/slots/bulk', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function cancelSlot(slotId: string) {
  return call<{ ok: true; slot: Slot }>(`/api/praxis/slots/${encodeURIComponent(slotId)}`, {
    method: 'DELETE',
  });
}

// Hard-delete für Termin-Vorschläge (verschwindet auch beim Patient aus der App)
export async function deleteTerminVorschlag(terminId: string) {
  return call(`/api/praxis/termine/${encodeURIComponent(terminId)}`, { method: 'DELETE' });
}

// ─── Badge-Counter ──────────────────────────────────────────────────

/**
 * Format, das im QR-Code steckt — gleiches Schema wie im Admin-Dashboard,
 * sodass beide Codes gegen denselben Resolver in der App funktionieren.
 */
export function praxisQrPayload(p: Pick<Praxis, 'id' | 'qr_token' | 'name'>): string {
  return JSON.stringify({
    praxis_id: p.id,
    qr_token: p.qr_token,
    praxis_name: p.name,
  });
}

export interface PraxisBadges {
  inbox_neu: number;
  /** Pro-Typ-Counts für separate Sidebar-Einträge. */
  krankmeldungen_neu?: number;
  folgerezepte_neu?: number;
  ueberweisungen_neu?: number;
  fragen_neu: number;
  termine_offen: number;
  termine_neue_antwort: number;
}

export async function getBadges() {
  return call<{ ok: true; badges: PraxisBadges }>('/api/praxis/badges');
}
